import { apiClient } from '.';

/**
 * 뉴스레터 API (사용자 페이지)
 *
 * 백오피스(`/admin/newsletter`)에서 등록한 배너 / 강조 섹션 / 뉴스레터 목록을 읽어옵니다.
 * 서버: NewsletterUiController (/api/ui/newsletters)
 *
 * 구독 CTA 문구는 백오피스 관리 대상이 아니므로 아래 기본값을 사용합니다.
 */

// 뉴스레터 카드 한 건
export interface NewsletterItem {
  newsletter_id: number;
  title: string;
  thumbnail_url: string;
  badges: string[];
  author: string;
  published_at: string; // ISO 8601
  external_url: string; // 스티비 등 외부 발행물 링크
}

export interface NewsletterListResponse {
  code: number;
  status: string;
  message: string;
  data: NewsletterItem[];
}

// 배너/문구 등 백오피스에서 교체 가능한 고정 콘텐츠
export interface NewsletterPageContent {
  banner_image_url: string;
  banner_eyebrow: string;
  banner_title: string;
  banner_font_color: string;

  featured_label: string;
  featured_title: string;
  featured_image_url: string;
  featured_overlay_title: string;
  featured_overlay_caption: string;
  /** 강조 카드를 눌렀을 때 열리는 뉴스레터 링크 */
  featured_url: string;

  subscription_title: string;
  subscription_button_text: string;
  subscription_url: string;
}

// 서버 응답 타입
interface NewsletterBannerResponseData {
  banner_id: number;
  caption: string | null;
  title: string | null;
  font_color_hex_code: string | null;
  banner_image_url: string | null;
}

interface NewsletterFeaturedResponseData {
  featured_id: number | null;
  caption: string | null;
  text: string | null;
  featured_image_url: string | null;
  card_title: string | null;
  card_caption: string | null;
  newsletter_id: number | null;
  newsletter_title: string | null;
  newsletter_external_url: string | null;
}

// ---------------------------------------------------------------------------
// 기본값 (백오피스에 아직 값이 없는 항목에 사용)
// ---------------------------------------------------------------------------

export const DEFAULT_PAGE_CONTENT: NewsletterPageContent = {
  banner_image_url: '',
  banner_eyebrow: '',
  banner_title: '',
  banner_font_color: '#ffffff',

  featured_label: '',
  featured_title: '',
  featured_image_url: '',
  featured_overlay_title: '',
  featured_overlay_caption: '',
  featured_url: '',

  subscription_title:
    '어렵던 의료 AI가 조금씩 선명해지는 이야기,\n매주 맞춤형 뉴스레터로 만나보세요.',
  subscription_button_text: '뉴스레터 구독하기',
  subscription_url: 'https://page.stibee.com/subscriptions/519825',
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

// 뉴스레터 목록 조회 (등록 완료된 항목만 최신순)
export const fetchNewsletters = async (): Promise<NewsletterItem[]> => {
  try {
    const { data } = await apiClient.get<NewsletterListResponse>(
      '/api/ui/newsletters/list',
      { withCredentials: true }
    );
    return data.data ?? [];
  } catch (error) {
    return [];
  }
};

// 뉴스레터 페이지 고정 콘텐츠 조회 (배너 + 강조 섹션)
export const fetchNewsletterPageContent = async (): Promise<NewsletterPageContent> => {
  const [banner, featured] = await Promise.all([
    fetchFirstBanner(),
    fetchFeatured(),
  ]);

  return {
    ...DEFAULT_PAGE_CONTENT,

    banner_image_url: banner?.banner_image_url || DEFAULT_PAGE_CONTENT.banner_image_url,
    banner_eyebrow: banner?.caption || DEFAULT_PAGE_CONTENT.banner_eyebrow,
    banner_title: banner?.title || DEFAULT_PAGE_CONTENT.banner_title,
    banner_font_color: banner?.font_color_hex_code || DEFAULT_PAGE_CONTENT.banner_font_color,

    featured_label: featured?.caption || DEFAULT_PAGE_CONTENT.featured_label,
    featured_title: featured?.text || DEFAULT_PAGE_CONTENT.featured_title,
    featured_image_url: featured?.featured_image_url || DEFAULT_PAGE_CONTENT.featured_image_url,
    featured_overlay_title: featured?.card_title || DEFAULT_PAGE_CONTENT.featured_overlay_title,
    featured_overlay_caption: featured?.card_caption || DEFAULT_PAGE_CONTENT.featured_overlay_caption,
    featured_url: featured?.newsletter_external_url || DEFAULT_PAGE_CONTENT.featured_url,
  };
};

// 배너는 여러 개 등록할 수 있지만 사용자 페이지에는 첫 번째 배너만 노출합니다.
const fetchFirstBanner = async (): Promise<NewsletterBannerResponseData | null> => {
  try {
    const { data } = await apiClient.get<{ data: NewsletterBannerResponseData[] }>(
      '/api/ui/newsletters/banners/list',
      { withCredentials: true }
    );
    return data.data?.[0] ?? null;
  } catch (error) {
    return null;
  }
};

const fetchFeatured = async (): Promise<NewsletterFeaturedResponseData | null> => {
  try {
    const { data } = await apiClient.get<{ data: NewsletterFeaturedResponseData }>(
      '/api/ui/newsletters/featured',
      { withCredentials: true }
    );
    return data.data ?? null;
  } catch (error) {
    return null;
  }
};

// 카드 캡션용 날짜 포맷 (2026년 8월 20일)
export const formatNewsletterDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  } catch (error) {
    return dateString;
  }
};
